/**
 * Contrato de teste E2E (doc 04 §6.5): Playwright espera `canvas[data-ready="true"]`
 * e conversa com `window.__rabbitTest`. Parâmetros ?seed= e ?speed= só valem fora de prod.
 */
import type Phaser from 'phaser';
import { createRng } from '@rabbit2/shared/sim';

export interface RunSnapshot {
  tick: number;
  phase: string;
  hp: number;
  level: number;
  luar: number;
  enemies: number;
  drafting: boolean;
  checksum: number;
  /** objetivo do capítulo (AC-M3-08): null no cap.1 */
  objetivo: {
    mineracao?: { feitos: number; meta: number };
    escolta?: { chegou: boolean; npcAlive: boolean };
    completo: boolean;
  } | null;
  /** posição do herói (AC-M4-06/ACC-7: prova de movimento por teclado) */
  heroX: number;
  heroY: number;
}

export interface RabbitTestApi {
  /** cena ativa (key) */
  scene(): string;
  /** fps médio atual do loop */
  fps(): number;
  /** seed efetiva da sessão (query ?seed= em dev/teste) */
  seed(): number;
  /** amostra do RNG compartilhado NESTE engine — cross-check de determinismo (AC-M0-02) */
  rngSample(seed: number, n: number): number[];
  /** snapshot da run ativa (RunScene) */
  run?(): RunSnapshot;
  /** roda a sim headless neste engine e devolve o checksum final (AC-M1-07) */
  simChecksum?(seed: number, ticks: number): number;
  /** completa o objetivo do capítulo neste engine (AC-M3-08): mineração/escolta cumpríveis no browser */
  objetivoDemo?(chapter: number): { phase: string; completo: boolean };
  /** exibe o overlay de draft (AC-M4-06): DOM para o axe-core inspecionar */
  demoDraft?(): void;
  /** ACC-4: "reduzir movimento" está ativo? */
  reducedMotion?(): boolean;
  /** ACC-4: tenta tremer a câmera; retorna false quando reduzir-movimento está ligado */
  demoShake?(): boolean;
  /** ACC-4: modo de inversão de cor ('invert' normal, 'desaturate' com reduzir-movimento) */
  inversionMode?(): 'invert' | 'desaturate';
  /** AC-M4-04: vence o Norno neste engine (browser) — cross-engine do boss final */
  nornoDemo?(): { fase: number; phase: string; ativados: number };
  /** hook de teste (RunScene): força a derrota para exercitar o fluxo de fim de run */
  kill?(): void;
  /** hook de teste (RunScene): avança N ticks determinísticos para screenshot estável (AC-M1-11) */
  advance?(n: number): void;
  /** snapshot do co-op (CoopScene): estado da sala do RunRoom autoritativo (AC-M2-07) */
  coop?(): {
    connected: boolean;
    players: number;
    myIdx: number;
    tick: number;
    heroes: { x: number; y: number }[];
    erro: string;
  };
}

declare global {
  interface Window {
    __rabbitTest?: RabbitTestApi;
  }
}

export function urlSeed(search: string = location.search): number {
  const q = new URLSearchParams(search).get('seed');
  const n = q === null ? NaN : Number(q);
  return Number.isFinite(n) ? n >>> 0 : (Math.random() * 0xffffffff) >>> 0; // fora da sim: Math.random é permitido
}

export function installTestApi(game: Phaser.Game, seed: number): void {
  window.__rabbitTest = {
    scene: () => game.scene.getScenes(true)[0]?.scene.key ?? 'nenhuma',
    fps: () => game.loop.actualFps,
    seed: () => seed,
    rngSample: (s: number, n: number) => {
      const rng = createRng(s);
      return Array.from({ length: n }, () => rng.nextU32());
    },
  };
}

export function urlStress(search: string = location.search): number {
  const n = Number(new URLSearchParams(search).get('stress') ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 2000) : 0;
}

/** marca o canvas como pronto — gate de TODO screenshot/asserção do Playwright */
export function markReady(game: Phaser.Game): void {
  game.canvas.setAttribute('data-ready', 'true');
}
